// src/styles/designTokens.js
// OurNet Design Tokens — single source of truth for all UI values
// Every colour, size, spacing, and breakpoint used in buttons, headers, and footers
// is defined here. Change a value here and it propagates everywhere.
//
// Three-layer system:
//   designTokens.js  →  StandardButton.jsx  →  zone components (headers/footers)
//
// To make the site more spacious: increase gap/paddingH values below
// To shift colours: update the hex values in colors
// To adjust breakpoints: update the breakpoints object (and keep in sync with useMediaQuery.js)
//
// Last updated: March 2026

export const tokens = {

  // ==========================================================================
  // COLOUR FAMILIES
  // Each colour has a semantic meaning — use the right colour for the right job
  // ==========================================================================
  colors: {

    // Context — switches with the active context (locality or groups)
    // Used for: Back buttons, context toggle pills, navigation
    contextLocality: '#024B99',   // Locality blue
    contextGroups:   '#BB4D9A',   // Groups mauve

    // Action — positive, creative, forward-moving
    // Used for: Create, Publish, Save, Post, More, Go to Top
    action:              '#4ECDC4',
    actionDilute:        'rgba(78, 205, 196, 0.25)',  // 25% — ghost variant fill (Share)
    actionDiluteBorder:  'rgba(78, 205, 196, 0.40)',  // ghost variant border
    actionDiluteText:    '#2a9d8f',                   // ghost variant text/icon colour

    // Identity — user presence and support
    // Used for: Profile circle, Help circle, Log In circle
    identity: '#F9A61B',          // Orange

    // Help — the universal "?" support affordance (footers + Modal shell default slot).
    // Same hue as `identity` today, kept as its own semantic token so Help can be
    // retinted without moving the profile circle. See LAYOUT.md § HelpButton.
    help: '#F9A61B',

    // Visiting — the temporary-visit ("wander mode") header indicator (Change 3 addendum-4):
    // recolours the locality name + chevron while a logged-in user is browsing away from home.
    // Same hue as `identity`/`help` today, kept as its own semantic token per that same
    // precedent — visiting can be retinted independently of Profile/Help. Not yet in
    // LAYOUT.md's Functional Colors table — flagged for ratification, see
    // docs/escalations/2026-09-02-visiting-color-token.md.
    visiting: '#F9A61B',

    // Notification alert — the header bell's colour while the member has unread notifications
    // (design note 2026-09-22 §3). Orange is LAYOUT's documented Help/Warning hue. Same hue as
    // `identity`/`help`/`visiting` today, kept as its own semantic token per that precedent. Not
    // red: `destructive` is the count pill's and the admin alerts' colour, and a red bell beside
    // the admin Shield's red dot would read as the same signal. Not yet in LAYOUT.md's Functional
    // Colors table — recorded in docs/doc-accuracy-register.md.
    notificationAlert: '#F9A61B',

    // Destructive — removal, cancellation, irreversible actions
    // Used for: Cancel, Delete, Report, Remove
    // NOTE: destructive variant always shows confirmation modal before executing
    destructive: '#DC2626',       // Red (toned Option A)

    // Utility — non-committal, exploratory actions
    // Used for: Preview, Save as Draft
    utility: '#5B52CC',           // Purple (toned Option A)

    // Admin — platform administration only
    // Used for: Admin shield button (only visible to users.is_sys_admin accounts)
    admin: '#059669',             // Green

    // Inline — no-container interaction buttons
    // Used for: Like, Dislike, Reply, Their Articles, Flag, drill-down arrows
    // These use the theme's secondary text colour so they feel like content, not chrome
    inlineText: 'var(--color-text-secondary)',

    // Star rating
    starFilled: '#F9A61B',        // Same as identity orange
    starEmpty:  'transparent',
    starStroke: '#F9A61B',

    // Backgrounds
    headerBackground: '#EDF2F8',  // Grey bar behind white content bar
    contentBar:       '#FFFFFF',  // White bar in header
    footerBackground: '#EDF2F8',  // Footer background
    directoryPanel:   '#FAFBFC',  // Barely-off-white — directory panel, stalk, closed hint strip

    // Backdrop — the scrim behind every modal. One canonical value (was 0.4 / 0.45 /
    // 0.5 / 0.7 across forked modals; 0.5 chosen — the conventional scrim opacity and
    // the most common of the observed values). See LAYOUT.md § Backdrop + Modal shell.
    backdrop: 'rgba(0, 0, 0, 0.5)',

    // Neutral (for text, borders in ghost/disabled states)
    textSecondary: '#6B7280',
    borderSecondary: '#D1D5DB',
    disabledBackground: '#E5E7EB',
    disabledText: '#9CA3AF',
    // GAP (noted June 2026): there is no near-black *primary* foreground token here.
    // The primary text colour lives on the theme object as `theme.text.primary`
    // (#1F2937, src/themes/index.js). The chat-board clickable-thread dot had to
    // pull from `theme.text.primary` because no equivalent token exists in this file.
    // If a foreground token is added, prefer it over the per-component theme read.
  },


  // ==========================================================================
  // BUTTON SIZING
  // Desktop and mobile sizes for each button shape type
  // ==========================================================================
  button: {

    // Circle buttons — Profile, Help, Admin, Log In
    // Shape: perfect circle with icon centred inside
    circle: {
      desktop: { diameter: 40, iconSize: 22 },  // matches logo height (40px)
      mobile:  { diameter: 40, iconSize: 22 },
    },

    // Pill buttons — Create, Back, Publish, Cancel, Preview, Save Draft, More, Top
    // Shape: rounded rectangle (borderRadius = height/2)
    pill: {
      desktop: {
        height:       40,
        iconSize:     20,
        fontSize:     13,
        paddingH:     20,   // horizontal padding (left and right)
        gap:           6,   // gap between icon and label text
        borderRadius: 20,
        fontWeight:  600,
      },
      mobile: {
        height:       40,
        iconSize:     18,
        fontSize:     13,
        paddingH:     16,
        gap:           6,
        borderRadius: 20,
        fontWeight:  600,
        insetBottom:  16,   // distance from bottom edge of viewport
      },
    },

    // Ghost buttons — Share (dilute teal background with border, subtle container)
    // Shape: rounded rectangle, smaller than pill
    ghost: {
      desktop: {
        height:       36,
        iconSize:     16,
        fontSize:     12,
        paddingH:     14,
        gap:           5,
        borderRadius: 20,
        fontWeight:  500,
      },
      mobile: {
        height:       32,
        iconSize:     14,
        fontSize:     11,
        paddingH:     12,
        gap:           4,
        borderRadius: 18,
        fontWeight:  500,
      },
    },

    // Inline buttons — Like, Dislike, Reply, Their Articles, Flag, drill-down
    // Shape: NO container (no background, no border, no padding)
    // These feel like part of the content, not UI chrome
    // The compact/lively feel of the platform comes largely from these
    inline: {
      desktop: { iconSize: 14, fontSize: 12, gap: 4, fontWeight: 400 },
      mobile:  { iconSize: 12, fontSize: 11, gap: 3, fontWeight: 400 },
    },

    // Inline-emphasis — a bolder sibling of `inline` for a card's single primary "go see
    // more" affordance (Events MiniCard's "Full details", and whatever News adopts when it
    // gets its own MiniCard) — still no container, but ~1.2x the base inline tier and bold
    // (600, matching typography.buttonWeightBold — written as a literal here since this
    // object can't reference itself yet, same as the pill tier's fontWeight below).
    // Callers resolve the actual colour via `inlineTextColor` (context colour), same
    // pattern MoreButton.jsx already uses for the base inline tier.
    'inline-emphasis': {
      desktop: { iconSize: 17, fontSize: 14, gap: 5, fontWeight: 600 },
      mobile:  { iconSize: 14, fontSize: 13, gap: 4, fontWeight: 600 },
    },
  },


  // ==========================================================================
  // MOBILE HEADER (shared left cluster: logo + context/back pill)
  // Single source of truth for the mobile header's logo + left pill so the feed header
  // (HeaderButtonsFeed) and the detail header (HeaderButtonsDetail) are dimensionally
  // identical — no logo/pill jump on feed↔detail transitions. Consumed via MobileHeaderLeftCluster.
  // ==========================================================================
  mobileHeader: {
    height: 50,        // Row 1 height
    edgeX: 12,         // horizontal padding to the logo (and to the right cluster)
    logoHeight: 32,    // header-logo.png height
    clusterGap: 8,     // gap between logo and pill
    pill: {
      height: 32,
      paddingX: 14,
      radius: 16,
      fontSize: 13,
      fontWeight: 700,
    },
  },


  // ==========================================================================
  // ICON
  // ==========================================================================
  icon: {
    strokeWidth: 2,   // Lucide default — consistent across all icons, do not override
  },


  // ==========================================================================
  // Z-INDEX SCALE
  // The single ladder every stacked / overlay element draws from. No raw z-index
  // literals in components — pick the tier whose meaning matches. Tiers are spaced
  // so they never collide. The current scattered literals (header 100/101,
  // footer 1100, dropdowns 1000, modals 1000 / 9999 / 10001+) migrate onto these
  // during the remediation track. Documented in LAYOUT.md § Z-index scale.
  // ==========================================================================
  zIndex: {
    base:     0,      // in-flow page content
    chrome:   100,    // fixed header / footer / bottom-bar frame (persistent chrome)
    popover:  200,    // content-anchored pop-outs (ContactCard, UtilityCategoryList drills)
    dropdown: 1000,   // chrome-anchored menus (ProfileMenu, NotificationsDropdown, IssuesDropdown)
    overlay:  1500,   // non-modal full-screen overlays and the floating Help button
    modal:    2000,   // modal backdrop + panel (one tier — backdrop and panel share it)
    toast:    3000,   // transient toasts / snackbars, above modals
  },


  // ==========================================================================
  // RESPONSIVE BREAKPOINTS
  // These mirror the values in src/hooks/useMediaQuery.js
  // IMPORTANT: keep in sync if either file is changed
  //
  // Key difference from simple mobile/desktop:
  //   isMobile in useMediaQuery returns true when windowWidth < 1200 (sidebarStart)
  //   That means the "mobile interface" (mobile headers/footers) kicks in at 1200px,
  //   not at 768px. This is because the sidebar disappears at 1200px.
  // ==========================================================================
  breakpoints: {
    phone:        768,   // below: phone layout (MobileBottomBar, drawer, phone header)
    tablet:      1024,   // 768-1023: tablet (uses mobile interface sizing for now)
    sidebarStart: 1200,  // 1200+: sidebar appears, full desktop interface
    sidebarFull:  1400,  // 1400+: sidebar at full 480px width (not compressed)
  },


  // ==========================================================================
  // ZONE DIMENSIONS
  // Header, footer, sidebar, and spacing measurements
  // ==========================================================================
  zones: {

    header: {
      desktop:            { height: 80, greyBarHeight: 4, contentBarHeight: 56 },
      mobile:             { height: 50 },         // single line
      mobileWithSecondLine: { height: 94, line1: 50, line2: 44 },  // feed mobile (mobile FP); legacy mobile header still 90px
    },

    footer: {
      // Height WHEN PRESENT — not a claim that every page has a footer. Footer presence is
      // conditional (per page / tab / breakpoint): e.g. the front page has no fixed footer.
      // Presence governance is deferred to the Header/Footer Behaviour standard; this token
      // only states the dimension where a footer does render.
      // Corrected July 2026: was a stale 80; FooterContainer renders 120 on desktop
      // (FooterContainer.jsx:108,157). Nothing read this token while it was wrong.
      desktop: { height: 120 },
      mobile:  { height: 60 },
    },

    sidebar: {
      maxWidth:    480,   // full width at 1400px+
      minWidth:    384,   // compressed at 1200px
      // Dynamic width calculated by useMediaQuery's calculateSidebarWidth()
    },

    spacing: {
      edgeMargin:       40,  // distance from window edge to outermost button (desktop)
      edgeMarginMobile: 12,  // distance from window edge to outermost button (mobile)
    },

  },


  // ==========================================================================
  // TYPOGRAPHY
  // Used by button labels — all other typography follows global CSS
  // ==========================================================================
  typography: {
    fontFamily: "'OpenSauceSans', sans-serif",  // OurNet only font — no exceptions
    buttonWeightNormal: 500,
    buttonWeightBold:   600,
    buttonLetterSpacing: '0.01em',
  },


  // ==========================================================================
  // HELP BUTTON
  // Size tokens for the shared HelpButton component (core/HelpButton.jsx).
  // Colour comes from tokens.colors.help. Size varies by render context.
  // Arbitrated July 2026: 32px chosen as canonical modal size (was a 32–36px
  // range across forked modals; 36px was auth-modal drift, now unified to 32).
  // ==========================================================================
  help: {
    sizeModal:  32,   // "?" inside a modal panel (bottom-right slot)
    sizeFooter: 50,   // "?" in the desktop footer and MobileBottomBar
  },


  // ==========================================================================
  // COMMENT THREAD (ConcertinaThread variant="comment")
  // Article/event comments render through the shared thread engine in its
  // inherited-headline, text-only skin. They sit NARROWER than locality Chat
  // (which fills its column) — this caps the readable measure under an article.
  // ==========================================================================
  commentThread: {
    maxWidth: '680px',   // narrower than the full Chat column; comfortable reading measure
  },


  // ==========================================================================
  // CARD
  // The single source for card-surface geometry. Card surfaces come from the shared
  // shared/Card.jsx (remediation track) in the variants below; these values consolidate
  // the drifted per-grid literals the librarian audit found (NewsGrid/EventsGrid squares
  // had disagreed on radius 8 vs 12 and shadow 0 2px 6px vs 0 2px 4px). See LAYOUT.md
  // § Card primitive. NOTE: NewsBar's rotating news-hero card (radius 6, 5-line clamp) is a
  // DISTINCT surface, not one of these variants — reconcile in remediation, do not force-fit.
  // ==========================================================================
  card: {
    radius: {
      square160: 8,    // mobile 160×160 grid card (NewsGrid used 8; EventsGrid drifted to 12 — 8 chosen)
      wide:      10,   // desktop news / feed card
      list:      8,    // list-row card
    },
    shadow: {
      resting: '0 2px 6px rgba(0, 0, 0, 0.1)',  // default (mobile square, list)
      raised:  '0 2px 8px rgba(0, 0, 0, 0.1)',  // desktop wide card (slightly higher)
    },
    square: {           // mobile 160×160 card — replaces the raw 160 / aspect / clamp in LAYOUT.md
      size:          160,      // px, fixed
      imageAspect:   '4 / 3',  // image occupies ~75% of card height
      headlineLines: 2,        // 2-line clamp
    },
    wide: {             // desktop feed card
      imageAspect:   '4 / 3',
      headlineLines: 2,
    },
  },

};

// ==========================================================================
// VARIANT MAP
// Maps semantic variant names to their token values.
// StandardButton reads this to determine colour and shape without a switch block.
// ==========================================================================
export const variantMap = {
  context: {
    shape:  'pill',
    // colour is dynamic — set by theme context (locality blue or groups mauve)
    // StandardButton resolves this from the ThemeProvider context
    textColor: '#FFFFFF',
    checkUnsaved: true,   // default for context (back) buttons
  },
  action: {
    shape:      'pill',
    color:      tokens.colors.action,
    textColor:  '#FFFFFF',
  },
  destructive: {
    shape:         'pill',
    color:         tokens.colors.destructive,
    textColor:     '#FFFFFF',
    confirmBefore: true,   // always confirms before executing
  },
  utility: {
    shape:     'pill',
    color:     tokens.colors.utility,
    textColor: '#FFFFFF',
  },
  identity: {
    shape:     'circle',
    color:     tokens.colors.identity,
    textColor: '#FFFFFF',
    // Special: if profileImage prop provided, shows image with orange border ring
  },
  ghost: {
    shape:       'ghost',
    color:       tokens.colors.actionDilute,
    borderColor: tokens.colors.actionDiluteBorder,
    textColor:   tokens.colors.actionDiluteText,
  },
  'ghost-action': {
    // Alias for ghost — kept for backwards compatibility
    shape:       'ghost',
    color:       tokens.colors.actionDilute,
    borderColor: tokens.colors.actionDiluteBorder,
    textColor:   tokens.colors.actionDiluteText,
  },
  inline: {
    shape:     'inline',   // no container — just icon + text
    color:     'transparent',
    textColor: tokens.colors.inlineText,
    // Hover: opacity 0.7 only, no background change
  },
  'inline-emphasis': {
    // A bolder sibling of `inline` (see tokens.button['inline-emphasis'] above) — same
    // no-container shape family, own sizing tier. Callers pass `inlineTextColor` for the
    // context colour, same as the base inline variant's own established convention.
    shape:     'inline-emphasis',
    color:     'transparent',
    textColor: tokens.colors.inlineText,
  },
  admin: {
    shape:     'circle',
    color:     tokens.colors.admin,
    textColor: '#FFFFFF',
  },
};


// ==========================================================================
// HELPER: get sizing for a shape at a breakpoint
// Usage in StandardButton:
//   const sz = getSizing(buttonShape, isMobile);
//   style={{ height: sz.height, fontSize: sz.fontSize }}
// ==========================================================================
export const getSizing = (shape, isMobile) => {
  const tier = isMobile ? 'mobile' : 'desktop';
  return tokens.button[shape]?.[tier] ?? tokens.button.pill[tier];
};


// ==========================================================================
// HELPER: get context colour from theme
// Usage: const contextColor = getContextColor(theme);
// ==========================================================================
export const getContextColor = (theme) => {
  // theme.primary is set by ThemeProvider based on Locality/Groups context
  return theme?.primary ?? tokens.colors.contextLocality;
};


export default tokens;
